import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Calendar, MapPin, Clock, Check, X, Plus, Crown, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Playdates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [playdates, setPlaydates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [participantId, setParticipantId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.is_premium) {
        setLoading(false);
        return;
      }

      try {
        const [playdatesRes, matchesRes] = await Promise.all([
          axios.get(`${API}/playdates`),
          axios.get(`${API}/matches`)
        ]);
        setPlaydates(playdatesRes.data.playdates || []);
        setMatches(matchesRes.data.matches || []);
      } catch (error) {
        console.error('Error fetching playdates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!participantId || !title || !location || !date || !time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const scheduledAt = new Date(`${date}T${time}`);
      await axios.post(`${API}/playdates`, {
        participant_id: participantId,
        title,
        description,
        location,
        scheduled_at: scheduledAt.toISOString()
      });
      
      toast.success("Playdate created! Waiting for confirmation.");
      setShowCreate(false);
      
      // Reset form
      setParticipantId("");
      setTitle("");
      setDescription("");
      setLocation("");
      setDate("");
      setTime("");
      
      // Refresh
      const response = await axios.get(`${API}/playdates`);
      setPlaydates(response.data.playdates || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create playdate");
    } finally {
      setCreating(false);
    }
  };

  const handleRespond = async (playdateId, status) => {
    try {
      await axios.put(`${API}/playdates/${playdateId}/respond?status=${status}`);
      toast.success(`Playdate ${status}!`);
      
      // Refresh
      const response = await axios.get(`${API}/playdates`);
      setPlaydates(response.data.playdates || []);
    } catch (error) {
      toast.error("Failed to respond");
    }
  };

  // Premium gate
  if (!user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] pb-24" data-testid="playdates-page">
        <header className="sticky top-0 z-40 glass border-b border-slate-100">
          <div className="max-w-lg mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Playdate Planner
              </h1>
              <div className="w-6" />
            </div>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-6 py-12 text-center">
          <div className="w-24 h-24 rounded-full premium-gradient flex items-center justify-center mx-auto mb-6">
            <Crown className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Premium Feature
          </h2>
          <p className="text-slate-600 mb-8">
            Upgrade to plan and manage playdates with your matched families. Coordinate meetups without sharing personal contact info!
          </p>
          <Link to="/subscription">
            <Button className="premium-gradient text-white rounded-full px-8 py-6 text-lg" data-testid="upgrade-btn">
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
          </Link>
        </main>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24" data-testid="playdates-page">
      {/* Decorative blobs */}
      <div className="blob blob-yellow w-64 h-64 -top-32 -right-32 fixed opacity-30" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Playdate Planner
            </h1>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <button className="p-2 bg-teal-500 text-white rounded-full hover:bg-teal-600" data-testid="create-playdate-btn">
                  <Plus className="w-5 h-5" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-white rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Plan a Playdate
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div>
                    <Label>With Family</Label>
                    <Select value={participantId} onValueChange={setParticipantId}>
                      <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="select-family">
                        <SelectValue placeholder="Select a matched family" />
                      </SelectTrigger>
                      <SelectContent>
                        {matches.map((match) => (
                          <SelectItem key={match.profile?.user_id} value={match.profile?.user_id}>
                            {match.profile?.parent_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Park playdate, Coffee meetup..."
                      className="mt-1 h-12 rounded-xl"
                      data-testid="playdate-title"
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Central Park, Local playground..."
                      className="mt-1 h-12 rounded-xl"
                      data-testid="playdate-location"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="mt-1 h-12 rounded-xl"
                        data-testid="playdate-date"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="mt-1 h-12 rounded-xl"
                        data-testid="playdate-time"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Any additional details..."
                      className="mt-1 rounded-xl"
                      data-testid="playdate-description"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-6"
                    data-testid="submit-playdate-btn"
                  >
                    {creating ? "Creating..." : "Send Invitation"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : playdates.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No playdates yet
            </h2>
            <p className="text-slate-500 mb-6">
              Plan your first playdate with a matched family!
            </p>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-teal-500 hover:bg-teal-600 text-white rounded-full"
              data-testid="plan-first-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Plan a Playdate
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {playdates.map((playdate) => (
              <div
                key={playdate.playdate_id}
                className="bg-white rounded-2xl p-5 shadow-sm"
                data-testid={`playdate-${playdate.playdate_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {playdate.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                      <Users className="w-4 h-4" />
                      <span>with {playdate.other_family?.parent_name}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    playdate.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    playdate.status === 'declined' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {playdate.status}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4 text-teal-500" />
                    <span>{format(new Date(playdate.scheduled_at), "EEEE, MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4 text-teal-500" />
                    <span>{format(new Date(playdate.scheduled_at), "h:mm a")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-teal-500" />
                    <span>{playdate.location}</span>
                  </div>
                </div>

                {playdate.description && (
                  <p className="text-sm text-slate-500 mt-3 p-3 bg-slate-50 rounded-xl">
                    {playdate.description}
                  </p>
                )}

                {/* Response buttons for invitations */}
                {!playdate.is_creator && playdate.status === 'pending' && (
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => handleRespond(playdate.playdate_id, 'accepted')}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl"
                      data-testid="accept-playdate-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept
                    </Button>
                    <Button
                      onClick={() => handleRespond(playdate.playdate_id, 'declined')}
                      variant="outline"
                      className="flex-1 rounded-xl text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="decline-playdate-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Playdates;